import { apiFetch } from '@/api/client';
import type {
  Article,
  ArticleReviewRun,
  ArticleRevision,
  ArticleStatus,
  AuthorityMatch,
  AuthorityOverride,
  BackpropEntry,
  ConvertResult,
  DisputeFlagResponse,
  ExternalLink,
  FlagAck,
  IllustrationGenerateInput,
  IllustrationOverride,
  IllustrationPlanEntry,
  IllustrationPreview,
  InternalLink,
  ResolutionCategory,
} from '@/types';

export type {
  AuthorityMatch,
  ConvertResult,
  IllustrationPreview,
  FlagAck,
  IllustrationGenerateInput,
  IllustrationOverride,
  IllustrationPlanEntry,
  BackpropEntry,
  ArticleReviewRun,
  ArticleRevision,
};

export interface IllustrationPreviewResponse {
  previews: IllustrationPreview[];
  // The article's edit stamp after a preview mutation (generate/remove). A
  // preview mutation is an unpublished edit, but this response carries the
  // hydrated previews rather than the whole article, so the editor reads the
  // fresh stamp from here to refresh its in-memory edited_at and open the
  // republish gate. Absent on read-only responses (the previews list).
  edited_at?: string;
  edited_by?: string;
}

export interface IllustrationPlanResponse {
  plan: IllustrationPlanEntry[];
  orphans: IllustrationOverride[];
}

export interface ArticleUpdateInput {
  title?: string;
  title_override?: string;
  slug?: string;
  meta_description?: string;
  body_md?: string;
  aeo_intent?: string;
  target_keywords?: string[];
}

export interface ArticlesListOptions {
  status?: ArticleStatus;
  topic?: string;
  includeArchived?: boolean;
  q?: string;
  sort?: 'date' | 'title' | 'score';
  order?: 'asc' | 'desc';
}

export interface ArticleDeleteResponse {
  status: 'archived' | 'deleted';
  id: string;
  commit_sha?: string;
}

export const articles = {
  list: (siteId: string, opts: ArticlesListOptions | ArticleStatus = {}) => {
    const params = new URLSearchParams({ site_id: siteId });
    const o = typeof opts === 'string' ? { status: opts } : opts;
    if (o.status) params.set('status', o.status);
    if (o.topic) params.set('topic', o.topic);
    if (o.includeArchived) params.set('include_archived', 'true');
    if (o.q) params.set('q', o.q);
    if (o.sort) params.set('sort', o.sort);
    if (o.order) params.set('order', o.order);
    return apiFetch<Article[]>(`/articles/?${params.toString()}`);
  },
  get: (id: string) => apiFetch<Article>(`/articles/${encodeURIComponent(id)}`),
  update: (id: string, patch: ArticleUpdateInput) =>
    apiFetch<Article>(`/articles/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  remove: (id: string, opts: { hard?: boolean } = {}) =>
    apiFetch<ArticleDeleteResponse>(
      `/articles/${encodeURIComponent(id)}${opts.hard ? '?hard=true' : ''}`,
      { method: 'DELETE' },
    ),
  bulkArchive: (ids: string[]) =>
    apiFetch<{ archived: number }>(`/articles/bulk`, {
      method: 'POST',
      body: JSON.stringify({ action: 'archive', ids }),
    }),
  exportCsvUrl: (siteId: string) =>
    `/api/articles/?${new URLSearchParams({ site_id: siteId, format: 'csv' }).toString()}`,
  publish: (id: string, waive?: string[]) =>
    apiFetch<{ status: string }>(`/articles/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: waive && waive.length ? JSON.stringify({ waive }) : undefined,
    }),
  unpublish: (id: string) =>
    apiFetch<Article>(`/articles/${encodeURIComponent(id)}/unpublish`, {
      method: 'POST',
    }),
  preview: (id: string) =>
    apiFetch<{ html: string }>(`/articles/${encodeURIComponent(id)}/preview`),
  previewMDX: (id: string) =>
    apiFetch<ConvertResult>(
      `/articles/${encodeURIComponent(id)}/preview?format=mdx`,
    ),
  listFlagAcks: (id: string) =>
    apiFetch<FlagAck[]>(`/articles/${encodeURIComponent(id)}/flags`),
  acknowledgeFlag: (
    id: string,
    index: number,
    body: { resolution_note: string; resolution_category: ResolutionCategory },
  ) =>
    apiFetch<FlagAck>(`/articles/${encodeURIComponent(id)}/flags/${index}/ack`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  disputeFlag: (
    id: string,
    index: number,
    body: { dispute_reason: string },
  ) =>
    apiFetch<DisputeFlagResponse>(
      `/articles/${encodeURIComponent(id)}/flags/${index}/dispute`,
      { method: 'POST', body: JSON.stringify(body) },
    ),
  authorityMatches: (id: string) =>
    apiFetch<AuthorityMatch[]>(
      `/articles/${encodeURIComponent(id)}/authority-matches`,
    ),
  illustrations: {
    generate: (id: string, body: IllustrationGenerateInput) =>
      apiFetch<IllustrationPreviewResponse>(
        `/articles/${encodeURIComponent(id)}/illustrations/generate`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    list: (id: string) =>
      apiFetch<IllustrationPreviewResponse>(
        `/articles/${encodeURIComponent(id)}/illustrations/previews`,
      ),
    // Keyed on the block's svg_ref, never on a position in the previews array:
    // a live article's previews are hydrated from disk, so their array indexes
    // are not addressable server-side (and used to 409, every time).
    remove: (id: string, svgRef: string) =>
      apiFetch<IllustrationPreviewResponse>(
        `/articles/${encodeURIComponent(id)}/illustrations/${encodeURIComponent(svgRef)}`,
        { method: 'DELETE' },
      ),
    // Write back an EXACT prior SVG (bytes + metadata) for a block, keyed on its
    // svg_ref, with no Claude call. Backs undo/redo: restoring a regenerated or
    // removed illustration to a snapshotted state. The backend re-runs the SVG
    // security validator on the supplied bytes.
    restore: (id: string, preview: IllustrationPreview) =>
      apiFetch<IllustrationPreviewResponse>(
        `/articles/${encodeURIComponent(id)}/illustrations/previews/${encodeURIComponent(preview.section)}`,
        { method: 'PUT', body: JSON.stringify(preview) },
      ),
  },
  illustrationPlan: {
    get: (id: string) =>
      apiFetch<IllustrationPlanResponse>(
        `/articles/${encodeURIComponent(id)}/illustration-plan`,
      ),
    update: (id: string, body: { overrides: IllustrationOverride[] }) =>
      apiFetch<Article>(
        `/articles/${encodeURIComponent(id)}/illustration-plan`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
  },
  interlinkPlan: {
    update: (
      id: string,
      body: { internal_links: InternalLink[]; external_links: ExternalLink[] },
    ) =>
      apiFetch<Article>(
        `/articles/${encodeURIComponent(id)}/interlink-plan`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
  },
  backpropagationPreview: (id: string) =>
    apiFetch<BackpropEntry[]>(
      `/articles/${encodeURIComponent(id)}/backpropagation-preview`,
    ),
  authorityOverrides: {
    update: (id: string, body: { overrides: AuthorityOverride[] }) =>
      apiFetch<Article>(
        `/articles/${encodeURIComponent(id)}/authority-overrides`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
  },
  reReviews: {
    list: (id: string) =>
      apiFetch<ArticleReviewRun[]>(
        `/articles/${encodeURIComponent(id)}/re-reviews`,
      ),
    run: (id: string) =>
      apiFetch<ArticleReviewRun>(
        `/articles/${encodeURIComponent(id)}/re-review`,
        { method: 'POST' },
      ),
  },
  revisions: {
    list: (id: string) =>
      apiFetch<ArticleRevision[]>(
        `/articles/${encodeURIComponent(id)}/revisions`,
      ),
    run: (
      id: string,
      body: { additional_instructions?: string; include_flags?: boolean } = {},
    ) =>
      apiFetch<ArticleRevision>(
        `/articles/${encodeURIComponent(id)}/revise`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    accept: (articleId: string, revisionId: string) =>
      apiFetch<Article>(
        `/articles/${encodeURIComponent(articleId)}/revisions/${encodeURIComponent(revisionId)}/accept`,
        { method: 'POST' },
      ),
    discard: (articleId: string, revisionId: string) =>
      apiFetch<ArticleRevision>(
        `/articles/${encodeURIComponent(articleId)}/revisions/${encodeURIComponent(revisionId)}/discard`,
        { method: 'POST' },
      ),
  },
};
